import React, { useEffect, useState, useRef, useContext } from "react";
import axios from "axios";
import { widget } from "../../Assets/charting_library/charting_library.min";
import * as Stomp from "stompjs";
import * as SockJS from "sockjs-client";
import { Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import InfoPopUp from "../../Common/InfoPopUp";
import { DataContext } from ".";
import { showToast } from "../../CoreDataService";
import InfoIcon from "@mui/icons-material/Info";

const title = "Chart Widget";
const description =
  "Displays trade chart for 1 minute,3 minutes, 5 minutes, 15 minutes, 30 minutes, 1 hour, 4 hours, 1 day, 2 days, 3 days, 1 week, 3 weeks, 1 month, 6 months, and years as well.";

const TvChartContainer = () => {
  const { currencycode, basecurrency, isPublicView } = useContext(DataContext);
  const logo = localStorage.getItem("exchangeLogo");
  const { userInfo } = useSelector((state) => state);
  const [show, setShow] = useState(false);

  // 🔹 INTERVAL FIX
  const intervalStorageKey = `tv_interval_spot`;
  const [interval, setInterval] = useState(
    localStorage.getItem(intervalStorageKey) || "60"
  );

  const [datafeedUrl, setDatafeedUrl] = useState(
    "https://accounts.paybito.com/ChartApi/spot"
  );
  const [libraryPath, setLibraryPath] = useState("/charting_library/");
  const [chartsStorageUrl, setChartsStorageUrl] = useState(
    "https://saveload.tradingview.com"
  );
  const [chartsStorageApiVersion, setChartsStorageApiVersion] = useState("1.1");
  const [clientId, setClientId] = useState("tradingview.com");
  const [userId, setUserId] = useState(localStorage.getItem("uuid"));
  const [fullscreen, setFullscreen] = useState(false);
  const [autosize, setAutosize] = useState(true);
  const [containerId, setContainerId] = useState("tv_chart_container");
  const [tvWidget, setTvWidget] = useState(null);
  const [Themecolor, setThemecolor] = useState(
    isPublicView ? "dark" : (localStorage.getItem("theme") || "dark")
  );
  const [isLoading, setLoading] = useState(false);
  const handleClose = () => setShow(false);

  const websocketRef = useRef(null);
  const realtimeCallbackRef = useRef(null);
  const lastBarRef = useRef(null);

  const webSocketEndPoint = "https://stream.paybito.com/ChartStream/ws";
  const topic = "/user/topic/stream/tradingView";
  const reqNoRef = useRef(null);
  const intervalRef = useRef(interval);

  /* ---------------- SAVE / LOAD LAYOUT ---------------- */

  const saveChartState = (widget) => {
    if (!widget) return;
    const chartKey = `tvChart_${userId}_${currencycode}/${basecurrency}`;
    widget.save((state) => {
      localStorage.setItem(chartKey, JSON.stringify(state));
      localStorage.setItem(`tvChart_${userId}_lastSaved`, chartKey);
      showToast("success", "Chart layout saved!");
    });
  };

  const loadChartState = (widget) => {
    if (!widget) return;
    const chartKey = `tvChart_${userId}_${currencycode}/${basecurrency}`;
    const savedState = localStorage.getItem(chartKey);
    if (savedState) {
      try {
        widget.load(JSON.parse(savedState));
      } catch (e) {
        console.error("Failed to load chart layout", e);
      }
    }
  };

  /* ---------------- WEBSOCKET ---------------- */

  const connectWebSocket = () => {
    if (!localStorage.getItem("access_token")) return;

    const socket = new SockJS(webSocketEndPoint);
    websocketRef.current = Stomp.over(socket);
    websocketRef.current.debug = null;

    websocketRef.current.connect({}, () => {
      websocketRef.current.subscribe(topic, onMessageReceived);
      subscribe();
    });
  };

  const disconnectWebSocket = () => {
    if (websocketRef.current?.connected) {
      websocketRef.current.disconnect();
    }
  };

  const sendMessage = (req) => {
    if (!websocketRef.current?.connected) return;
    websocketRef.current.send(
      "/app/sendRequest/tradingView",
      {},
      JSON.stringify(req)
    );
  };

  const mapResolution = (res) => {
    const resolutionMap = {
      "1": "1MIN", "3": "3MIN", "5": "5MIN", "15": "15MIN", "30": "30MIN",
      "60": "1HR", "240": "4HR",
    };
    return resolutionMap[res] || res;
  };

  const unsubscribe = () => {
    if (reqNoRef.current == null) return;
    sendMessage({
      method: "UNSUBSCRIBE",
      base: basecurrency,
      counter: currencycode,
      actualResolution: mapResolution(intervalRef.current),
      id: reqNoRef.current,
    });
  };

  const subscribe = (resolution) => {
    const res = resolution || intervalRef.current;
    reqNoRef.current = Math.floor(Math.random() * 1000) + 1;
    sendMessage({
      method: "SUBSCRIBE",
      base: basecurrency,
      counter: currencycode,
      actualResolution: mapResolution(res),
      id: reqNoRef.current,
    });
  };

  const onMessageReceived = (message) => {
    try {
      const parsed = JSON.parse(message.body);
      if (parsed?.t) updateRealtimeBar(parsed);
    } catch {}
  };

  const updateRealtimeBar = (data) => {
    if (!realtimeCallbackRef.current) return;

    const bar = {
      time: data.t[0] * 1000,
      open: Number(data.o[0]),
      high: Number(data.h[0]),
      low: Number(data.l[0]),
      close: Number(data.c[0]),
      volume: Number(data.v[0]) * 0.7,
    };

    if (lastBarRef.current?.time === bar.time) {
      bar.open = lastBarRef.current.open;
      bar.high = Math.max(bar.high, lastBarRef.current.high);
      bar.low = Math.min(bar.low, lastBarRef.current.low);
    }

    lastBarRef.current = bar;
    realtimeCallbackRef.current(bar);
  };

  /* ---------------- DATAFEED ---------------- */

  const createDatafeed = () => ({
    onReady: (cb) =>
      cb({
        supported_resolutions: [
          "1",
          "3",
          "5",
          "15",
          "30",
          "60",
          "240",
          "1D",
          "2D",
          "3D",
          "1W",
          "3W",
          "1M",
          "6M",
        ],
      }),

    resolveSymbol: (symbolName, onResolve) => {
      const split = symbolName.split(/[:/]/);
      onResolve({
        name: symbolName,
        description: `${split[1]}/${split[2]}`,
        type: "crypto",
        session: "24x7",
        timezone: "Etc/UTC",
        ticker: symbolName,
        exchange: split[0],
        minmov: 1,
        pricescale: 100,
        has_intraday: true,
        supported_resolutions: [
          "1",
          "3",
          "5",
          "15",
          "30",
          "60",
          "240",
          "1D",
          "2D",
          "3D",
          "1W",
          "3W",
          "1M",
          "6M",
        ],
        volume_precision: 8,
      });
    },

    getBars: async (symbolInfo, resolution, from, to, onHistory, onError) => {
      try {
        const response = await axios.get(
          `${datafeedUrl}/history?symbol=${currencycode}%2F${basecurrency}&resolution=${resolution}&from=${from}&to=${to}`
        );

        if (!response.data?.c?.length) {
          onHistory([], { noData: true });
          return;
        }

        const bars = response.data.c.map((c, i) => ({
          open: Number(response.data.o[i]),
          high: Number(response.data.h[i]),
          low: Number(response.data.l[i]),
          close: Number(c),
          volume: Number(response.data.v[i]) * 0.7,
          time: Number(response.data.t[i]) * 1000,
        }));

        bars.sort((a, b) => a.time - b.time);

        lastBarRef.current = bars[bars.length - 1];

        onHistory(bars, { noData: false });
      } catch (e) {
        console.error(e);
        onError(e);
      }
    },

    subscribeBars: (_, __, onRealtime) => {
      realtimeCallbackRef.current = onRealtime;
    },

    unsubscribeBars: () => {
      realtimeCallbackRef.current = null;
      lastBarRef.current = null;
    },
  });

  /* ---------------- INIT CHART ---------------- */

  useEffect(() => {
    setLoading(true);
    if (tvWidget) tvWidget.remove();

    const widgetOptions = {
      symbol: `:${currencycode}/${basecurrency}`,
      datafeed: createDatafeed(),
      interval: interval,
      container_id: containerId,
      library_path: libraryPath,
      locale: "en",
      disabled_features: [
        "header_compare",
        "header_symbol_search",
        "widget_logo",
      ],
      enabled_features: [
        "hide_left_toolbar_by_default",
        "header_indicators",
        "header_chart_type",
        "save_chart_properties_to_local_storage",
        "create_volume_indicator_by_default",
        "use_localstorage_for_settings",
        "header_saveload",
      ],
      client_id: clientId,
      user_id: userId,
      fullscreen,
      autosize,
      theme: Themecolor,
    };

    const newTvWidget = new widget(widgetOptions);

    newTvWidget.onChartReady(() => {
      // 🔹 restore saved layout
      loadChartState(newTvWidget);

      // 🔹 SAVE LAYOUT BUTTON (restored)
      newTvWidget.headerReady().then(() => {
        const saveButton = newTvWidget.createButton();
        saveButton.textContent = "Save Layout";
        saveButton.addEventListener("click", () => {
          saveChartState(newTvWidget);
        });
      });

      // 🔹 save interval on change (SAFE)
      newTvWidget
        .chart()
        .onIntervalChanged()
        .subscribe(null, (newInterval) => {
          unsubscribe();
          intervalRef.current = newInterval;
          localStorage.setItem(intervalStorageKey, newInterval);
          setInterval(newInterval);
          subscribe(newInterval);
        });

      setLoading(false);
    });

    setTvWidget(newTvWidget);
    disconnectWebSocket();
    connectWebSocket();

    return () => {
      newTvWidget.remove();
      disconnectWebSocket();
    };
  }, [currencycode, basecurrency]);

  return (
    <div style={{ position: "relative" }}>
      <div id={containerId} style={{ height: "402px" }} />
      <InfoPopUp
        open={show}
        onClose={handleClose}
        title={title}
        description={description}
      />
    </div>
  );
};

export default TvChartContainer;
